import type { BackgroundStyle } from "@/cms/appearance";
import type { VideoComponentProps } from "@/components/ui/VideoComponent";
import { record, safeUrl } from "@/purpose-sections/model";
import {
  resolveLivingBackground,
  type LivingBackground,
} from "./living-background";

export { isCssColour } from "./css-colour";

export type Tint = NonNullable<BackgroundStyle["tint"]>;

export interface ResolvedBackground {
  kind: NonNullable<BackgroundStyle["kind"]>;
  color?: string;
  image?: string;
  mobileImage?: string;
  position?: string;
  video?: BackgroundStyle["video"];
  tint: Tint;
  overlayColor?: string;
  gradient?: BackgroundStyle["gradient"];
  fade?: BackgroundStyle["fade"];
  living?: LivingBackground;
}

export interface BackgroundOptions {
  /** The look system's Levende (`kind: "living"`); without it a living background resolves to none, as before. */
  living?: boolean;
}

type VideoType = NonNullable<BackgroundStyle["video"]>["type"];

const TINT_COLOURS: Record<Tint, string | undefined> = {
  none: undefined,
  dark: "rgba(0, 0, 0, 0.45)",
  light: "rgba(255, 255, 255, 0.55)",
};
const VIDEO_TYPES: readonly VideoType[] = ["realequity", "youtube", "vimeo"];

function text(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function finite(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

const GRADIENT_TYPES = ["linear", "radial", "conic"] as const;
const GRADIENT_POSITION = /^[a-z0-9\s.%-]+$/i;

function gradientOf(value: unknown): BackgroundStyle["gradient"] {
  const gradient = record(value);
  if (!Array.isArray(gradient.steps) || gradient.steps.length < 2)
    return undefined;
  const steps: NonNullable<BackgroundStyle["gradient"]>["steps"] = [];
  for (const raw of gradient.steps) {
    const step = record(raw);
    const color = text(step.color);
    if (!color) return undefined;
    steps.push({ color, offset: finite(step.offset) });
  }
  const type = GRADIENT_TYPES.find((candidate) => candidate === gradient.type);
  const position = text(gradient.position);
  return {
    ...(type && type !== "linear" ? { type } : {}),
    angle: finite(gradient.angle),
    ...(type &&
    type !== "linear" &&
    position &&
    GRADIENT_POSITION.test(position)
      ? { position }
      : {}),
    steps,
  };
}

function fadeOf(value: unknown): BackgroundStyle["fade"] {
  if (value === null || typeof value !== "object" || Array.isArray(value))
    return undefined;
  const fade = record(value);
  return {
    color: text(fade.color),
    start: finite(fade.start),
    end: finite(fade.end),
  };
}

function videoOf(value: unknown): ResolvedBackground["video"] {
  const video = record(value);
  const url = safeUrl(video.url);
  const type = VIDEO_TYPES.find((candidate) => candidate === video.type);
  if (!url || !type) return undefined;
  const flag = (key: string, fallback: boolean) =>
    typeof video[key] === "boolean" ? (video[key] as boolean) : fallback;
  return {
    url,
    type,
    loop: flag("loop", true),
    controls: flag("controls", false),
    autoplay: flag("autoplay", true),
    muted: flag("muted", true),
  };
}

function mediaTint(value: unknown): Tint {
  return value === "none" || value === "light" ? value : "dark";
}

/** Never throws. A kind without the content it renders resolves to `{ kind: "none", tint: "none" }`. */
export function resolveBackground(
  input: unknown,
  options: BackgroundOptions = {},
): ResolvedBackground {
  const value = record(input);
  const fade = fadeOf(value.fade);
  switch (value.kind) {
    case "living": {
      const living = options.living ? resolveLivingBackground(value) : null;
      if (living)
        return {
          kind: "living",
          living,
          ...(living.color ? { color: living.color } : {}),
          tint: "none",
          fade,
        };
      break;
    }
    case "colour": {
      const color = text(value.color);
      if (color) return { kind: "colour", color, tint: "none", fade };
      break;
    }
    case "gradient": {
      const gradient = gradientOf(value.gradient);
      if (gradient) return { kind: "gradient", gradient, tint: "none", fade };
      break;
    }
    case "image": {
      const image = safeUrl(value.image);
      if (image)
        return {
          kind: "image",
          image,
          mobileImage: safeUrl(value.mobileImage),
          position: text(value.position),
          tint: mediaTint(value.tint),
          overlayColor: text(value.overlayColor),
          fade,
        };
      break;
    }
    case "video": {
      const video = videoOf(value.video);
      if (video)
        return {
          kind: "video",
          video,
          image: safeUrl(value.image),
          position: text(value.position),
          tint: mediaTint(value.tint),
          overlayColor: text(value.overlayColor),
          fade,
        };
      break;
    }
  }
  return { kind: "none", tint: "none" };
}

export function tintCss(tint: Tint, overlayColor?: string): string | undefined {
  return overlayColor || TINT_COLOURS[tint];
}

/** A video covering its layer, as the media behind Medie og tekst's text and Mediemosaik's tiles draw it: the poster
 *  shows until it plays and alone under reduced motion, and the controls are shown only with `controls`. */
export function coverVideoConfig(
  video: NonNullable<ResolvedBackground["video"]>,
  poster?: string,
  position?: string,
  posterPriority?: VideoComponentProps["posterPriority"],
): VideoComponentProps {
  return {
    url: video.url,
    type: video.type,
    presentation: "cover",
    poster,
    ...(posterPriority ? { posterPriority } : {}),
    position,
    height: "100%",
    width: "100%",
    coverHeight: "100%",
    autoPlay: video.autoplay ?? true,
    muted: video.muted ?? true,
    loop: video.loop ?? true,
    controls: video.controls === true,
    showPlayPauseControl: video.controls === true,
  };
}
